import React, { useState } from "react";
import { toast } from "sonner";
import client, { formatApiError, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GoogleButton } from "@/components/GoogleButton";
import { WalletPicker } from "@/components/wallet/WalletPicker";
import { signWalletChallenge, walletErrorMessage } from "@/lib/wallet";

// Re-confirms identity before sensitive changes (linking/unlinking wallets, sign-in changes).
export function StepUpDialog({ open, onClose, onDone, methods = [] }) {
  const { user } = useAuth();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const has = (t) => methods.some((m) => m.type === t);

  const finish = (token) => { setToken(token); setPw(""); onDone(); };
  const viaPassword = async (e) => {
    e.preventDefault(); setBusy(true);
    try { finish((await client.post("/auth/step-up/password", { password: pw })).data.token); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  const viaWallet = async (provider) => {
    setBusy(true);
    try {
      const signed = await signWalletChallenge(provider, "reauth");
      setPicker(false);
      finish((await client.post("/auth/step-up/wallet", signed)).data.token);
    } catch (ex) { toast.error(walletErrorMessage(ex)); } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open && !picker} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="stepup-dialog" className="bg-[#0b0b0b] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Confirm it's you</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">For your security, please re-confirm your identity before changing sign-in settings.</DialogDescription>
          </DialogHeader>
          {user?.has_password && (
            <form onSubmit={viaPassword} className="space-y-3">
              <input data-testid="stepup-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Your password"
                className="w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" />
              <button data-testid="stepup-password-submit" disabled={busy} className="w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm disabled:opacity-60">Confirm</button>
            </form>
          )}
          {has("wallet") && (
            <button data-testid="stepup-wallet" onClick={() => setPicker(true)} className="w-full py-3 rounded-full border border-white/15 text-sm font-semibold hover:border-[#ccff00]/50">
              Confirm with a linked wallet
            </button>
          )}
          {has("google") && <GoogleButton label="Sign in again with Google" />}
        </DialogContent>
      </Dialog>
      <WalletPicker open={picker} onOpenChange={setPicker} onPick={viaWallet} busy={busy} title="Confirm with a linked wallet" />
    </>
  );
}
